import type { Integrations } from '@/integrations';
import { useAuthToken } from '@/services/auth';
import { useIntegrationsStore } from '@/store/integrations';
import { useProjectStore } from '@/store/project';
import { apiBasePath } from '@/utils/apiBasePath';
import type { IntegrationListing } from '@teemill/integrations';
import { Configuration, IntegrationsApi } from '@teemill/integrations';
import {
  useInstallation as moduleUseInstallation,
  useInstallations as moduleUseInstallations,
  useIntegration as moduleUseIntegration,
  useIntegrations as moduleUseIntegrations,
  setupIntegrations,
  type IntegrationConfig,
  type IntegrationInstallation,
  type ResolvedIntegration,
} from '@teemill/modules/integration-framework';
import { useMenu } from '@teemill/modules/menu';
import { computedAsync, computedEager } from '@vueuse/core';
import { merge, pick } from 'lodash';
import type { ComputedRef, Plugin } from 'vue';
import { computed, watch, type App } from 'vue';
import type { Router } from 'vue-router';
import { useProject, useProjects } from './projects';

const client = new IntegrationsApi(
  new Configuration({
    basePath: apiBasePath(),
    accessToken: () => useAuthToken(),
    baseOptions: {
      headers: {
        'User-Agent': null,
      },
    },
  }),
);

export const useIntegration = <K extends keyof Integrations>(code: K) =>
  moduleUseIntegration(code, useIntegrationsStore()) as ComputedRef<
    ResolvedIntegration<Integrations[K]>
  >;

export const useIntegrations = () => {
  const integrations = moduleUseIntegrations(useIntegrationsStore());

  return {
    ...integrations,
    client,
    sync: (name: keyof Integrations) => {
      const projects = useProjects();
      const project = useProject({ required: true });

      return projects.integrations
        .get(project.value.id, name)
        .then(({ data }) => integrations.patch(name, data.config));
    },
  };
};

export const useInstallation = <K extends keyof Integrations>(code: K) =>
  moduleUseInstallation(code, useIntegrationsStore()) as ComputedRef<
    IntegrationInstallation<Integrations[K]> | undefined
  >;

export const useInstallationConfig = <K extends keyof Integrations>(code: K) => {
  return computedEager(() => {
    const installation = useInstallation(code);

    return installation.value?.extendedConfig;
  }) as ComputedRef<IntegrationConfig<Integrations[K]>>;
};

export const useInstallations = () => moduleUseInstallations(useIntegrationsStore());

const listings = computedAsync(
  async () => {
    const projectStore = useProjectStore();

    if (!projectStore.active) {
      return [];
    }

    return client
      .getIntegrationListings({ project: projectStore.active.id })
      .then(({ data }) => data.listings);
  },
  [],
  {
    lazy: true,
  },
);

export const useListings = () => ({
  listings,
});

export const useListing = (code: string) =>
  computed(() => listings.value.find((listing) => listing.code === code));

export const setListing = (listingToSet: IntegrationListing) => {
  const listingIndex = listings.value.findIndex((listing) => listing.code === listingToSet.code);

  listings.value.splice(listingIndex, 1, listingToSet);
};

export const integrations: Plugin = {
  install(app: App, { router, integrations }: { router: Router; integrations: Integrations }) {
    const projects = useProjects();

    const projectStore = useProjectStore();
    const integrationsStore = useIntegrationsStore();

    integrationsStore.setIntegrations(integrations);

    setupIntegrations({
      app,
      store: integrationsStore,
      router: router,
      menu: useMenu(),
      resolver: async (integration) => integration,
    });

    const { install, uninstall } = useIntegrations();
    const { installations } = useInstallations();

    watch(
      () => ({ projectReady: projectStore.ready, project: projectStore.active }),
      async (data, previous) => {
        const { projectReady, project } = data;
        /**
         * ? Skips double loading integrations if watching is triggered
         * ? without a project change.
         */
        if (!projectReady) {
          return;
        }

        if (projectReady === previous?.projectReady && project?.id === previous?.project?.id) {
          return;
        }

        integrationsStore.ready = false;

        /**
         * Uninstall any integrations currently installed, as we're switching to a new project.
         * ? This is a frontend only operation, no integrations will be uninstalled for a project.
         */
        await Promise.all(
          installations.value.map((installation) => uninstall(installation.integration.code)),
        );

        /**
         * If a project is not active don't continue with fetch
         * integrations as there will not be any.
         */
        if (project === undefined) {
          integrationsStore.ready = true;
          return;
        }

        /**
         * Refetch the all integrations (Some might be hidden for projects)
         * Fetch all installed integrations
         * ? Async fetch both resources but process then sync to avoid
         * ? update issues.
         */
        const projectInstallations = await projects.integrations.list({
          project: project.id,
        });

        /**
         * Fetch the projects installed integrations
         */
        await Promise.all(
          projectInstallations.data.integrations.map(
            async ({ code, config, setupAt, metadata }): Promise<void> => {
              const integration = useIntegration(code as any);

              if (integration.value) {
                await install(integration.value.code, (c) => merge(c, config), {
                  setupAt: setupAt ? new Date(setupAt) : undefined,
                  metadata: metadata ? pick(metadata, ['title', 'sortOrder']) : undefined,
                });
              }
            },
          ),
        );

        integrationsStore.ready = true;
      },
      { immediate: true },
    );
  },
};

export const useProducts = (project?: string) => {
  return {
    list: (integration: number) =>
      client.getIntegrationProducts({ integration, project }).then(({ data }) => data),
  };
};
